import React, { useEffect, useState } from 'react';
import socketService from './socketService.js';
import { useNavigate } from 'react-router-dom';
import NotificationIcon from '@/common/assets/images/Notification.svg';
import styles from '../../../common/Layout/index.module.less';

const NotificationComponent = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [readNotifications, setReadNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [activeTab, setActiveTab] = useState('unread');

  useEffect(() => {
    // Initialize with stored notifications
    const storedNotifications =
      // JSON.parse(localStorage.getItem('notifications')) || [];
      JSON.parse(localStorage.getItem('notifications'))?.map((n) => ({
        ...n,
        receivedAt: new Date(n.receivedAt)
      })) || [];
    const storedReadNotifications =
      // JSON.parse(localStorage.getItem('readNotifications')) || [];
      JSON.parse(localStorage.getItem('readNotifications'))?.map((n) => ({
        ...n,
        receivedAt: new Date(n.receivedAt)
      })) || [];

    if (storedNotifications.length > 0) {
      // Only keep unread notifications in the notifications state
      const unreadNotifications = storedNotifications.filter((n) => !n.read);
      setNotifications(unreadNotifications);
    }

    if (storedReadNotifications.length > 0) {
      setReadNotifications(storedReadNotifications);
    }

    // Set up listeners
    const notificationUnsubscribe =
      socketService.addNotificationListener(handleNotifications);
    const pendingNotificationUnsubscribe =
      socketService.addPendingNotificationListener(handlePendingNotifications);
    const unreadNotificationsUnsubscribe =
      socketService.addUnreadNotificationsListener(handleUnreadNotifications);
    const readNotificationsUnsubscribe =
      socketService.addReadNotificationsListener(handleReadNotifications);

    const fetchNotifications = async () => {
      try {
        await socketService.getUnreadNotifications();
        await socketService.getReadNotifications();
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    // Wait for socket to connect, then fetch notifications
    if (!socketService.isConnected) {
      socketService.onConnect(() => {
        fetchNotifications();
      });
    } else {
      fetchNotifications();
    }

    return () => {
      notificationUnsubscribe();
      pendingNotificationUnsubscribe();
      unreadNotificationsUnsubscribe();
      readNotificationsUnsubscribe();
    };
  }, []);

  // Update hasUnread whenever notifications change
  useEffect(() => {
    setHasUnread(notifications.length > 0);
  }, [notifications]);

  // Handle regular notifications from localStorage
  const handleNotifications = (updatedNotifications) => {
    // Filter notifications based on read status
    const unread = updatedNotifications.filter((n) => !n.read);
    const read = updatedNotifications.filter((n) => n.read);

    setNotifications(unread);

    // Ensure we don't add duplicates to readNotifications
    const existingReadIds = new Set(readNotifications.map((n) => n.id));
    const newReadNotifications = read.filter((n) => !existingReadIds.has(n.id));
    if (newReadNotifications.length > 0) {
      setReadNotifications((prev) => [...newReadNotifications, ...prev]);
    }
  };

  // Handle unread notifications specifically
  const handleUnreadNotifications = (unreadNotifications) => {
    // Ensure we only set notifications that are actually unread
    const filteredUnread = unreadNotifications.filter((n) => !n.read);
    setNotifications(filteredUnread);
  };

  // Handle read notifications
  const handleReadNotifications = (readNotifications) => {
    // Prevent duplicates in read notifications
    const existingIds = new Set(readNotifications.map((n) => n.id));
    const uniqueReadNotifications = readNotifications.filter(
      (n) => !existingIds.has(n.id) || existingIds.delete(n.id)
    );
    setReadNotifications(uniqueReadNotifications);
  };

  const handlePendingNotifications = (pendingNotifications) => {
    const pendingWithTimestamps = pendingNotifications.map((notification) => ({
      ...notification,
      // receivedAt: notification.receivedAt || new Date(),
      // receivedAt: notification.receivedAt
      //   ? new Date(notification.receivedAt).toISOString()
      //   : new Date().toISOString(),
      receivedAt: new Date().toISOString(),

      read: false
    }));

    // Update state - only add to unread if actually unread
    const unreadPending = pendingWithTimestamps.filter((n) => !n.read);
    const readPending = pendingWithTimestamps.filter((n) => n.read);

    // Prevent duplicates in unread list
    const unreadIds = new Set(notifications.map((n) => n.id));
    const uniqueUnreadPending = unreadPending.filter(
      (n) => !unreadIds.has(n.id)
    );

    if (uniqueUnreadPending.length > 0) {
      setNotifications((prev) => [...uniqueUnreadPending, ...prev]);
    }

    // Prevent duplicates in read list
    if (readPending.length > 0) {
      const readIds = new Set(readNotifications.map((n) => n.id));
      const uniqueReadPending = readPending.filter((n) => !readIds.has(n.id));
      if (uniqueReadPending.length > 0) {
        setReadNotifications((prev) => [...uniqueReadPending, ...prev]);
      }
    }

    // Save to localStorage
    const storedNotifications =
      JSON.parse(localStorage.getItem('notifications')) || [];
    const existingIds = new Set(storedNotifications.map((n) => n.id));
    const newNotifications = pendingWithTimestamps.filter(
      (n) => !existingIds.has(n.id)
    );
    const updatedNotifications = [...newNotifications, ...storedNotifications];
    localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
  };

  const markAsRead = (notificationId) => {
    console.log('Marking as read:', notificationId);
    socketService.markNotificationsRead([notificationId]);

    // Find the notification to be marked as read
    const notificationToMove = notifications.find(
      (notification) => notification.id === notificationId
    );

    if (notificationToMove) {
      // Mark as read and add to read notifications
      const updatedNotification = { ...notificationToMove, read: true };

      // Update unread notifications
      setNotifications((prev) =>
        prev.filter((notification) => notification.id !== notificationId)
      );

      // Check if this notification is already in readNotifications to avoid duplicates
      const alreadyExists = readNotifications.some(
        (n) => n.id === notificationId
      );
      if (!alreadyExists) {
        // Add to read notifications if it doesn't exist there yet
        setReadNotifications((prev) => [updatedNotification, ...prev]);
      }

      // Update localStorage for both unread and read
      const storedNotifications =
        JSON.parse(localStorage.getItem('notifications')) || [];
      const updatedNotifications = storedNotifications.map((notification) =>
        notification.id === notificationId
          ? { ...notification, read: true }
          : notification
      );
      localStorage.setItem(
        'notifications',
        JSON.stringify(updatedNotifications)
      );

      // Update read notifications in localStorage, avoiding duplicates
      const storedReadNotifications =
        JSON.parse(localStorage.getItem('readNotifications')) || [];

      // Check if this notification is already in stored read notifications
      const storedAlreadyExists = storedReadNotifications.some(
        (n) => n.id === notificationId
      );

      if (!storedAlreadyExists) {
        const updatedReadNotifications = [
          updatedNotification,
          ...storedReadNotifications
        ];
        localStorage.setItem(
          'readNotifications',
          JSON.stringify(updatedReadNotifications)
        );
      }
    }
  };

  // Mark all notifications as read
  const markAllAsRead = () => {
    const unreadNotifications = notifications.filter(
      (notification) => !notification.read
    );

    if (unreadNotifications.length === 0) return;

    const unreadIds = unreadNotifications.map(
      (notification) => notification.id
    );

    console.log('Marking all as read:', unreadIds);
    socketService.markNotificationsRead(unreadIds);

    // Mark all as read and move to read notifications
    const markedAsRead = unreadNotifications.map((notification) => ({
      ...notification,
      read: true
    }));

    // Update unread notifications - clear only those that were marked as read
    setNotifications([]);

    // Add to read notifications, avoiding duplicates
    const existingReadIds = new Set(readNotifications.map((n) => n.id));
    const uniqueMarkedAsRead = markedAsRead.filter(
      (n) => !existingReadIds.has(n.id)
    );

    if (uniqueMarkedAsRead.length > 0) {
      setReadNotifications((prev) => [...uniqueMarkedAsRead, ...prev]);
    }

    // Update localStorage
    const storedNotifications =
      JSON.parse(localStorage.getItem('notifications')) || [];
    const updatedNotifications = storedNotifications.map((notification) =>
      unreadIds.includes(notification.id)
        ? { ...notification, read: true }
        : notification
    );
    localStorage.setItem('notifications', JSON.stringify(updatedNotifications));

    // Update read notifications in localStorage, avoiding duplicates
    const storedReadNotifications =
      JSON.parse(localStorage.getItem('readNotifications')) || [];

    // Filter out notifications that already exist in stored read notifications
    const storedReadIds = new Set(storedReadNotifications.map((n) => n.id));
    const uniqueMarkedForStorage = markedAsRead.filter(
      (n) => !storedReadIds.has(n.id)
    );

    if (uniqueMarkedForStorage.length > 0) {
      const updatedReadNotifications = [
        ...uniqueMarkedForStorage,
        ...storedReadNotifications
      ];
      localStorage.setItem(
        'readNotifications',
        JSON.stringify(updatedReadNotifications)
      );
    }
  };

  // Toggle notification dropdown
  const toggleDropdown = () => {
    setShowDropdown((prev) => !prev);
  };

  // Format notification time - MODIFIED
  // const formatTime = (date) => {
  //   if (!date) return '';
  //   try {
  //     const now = new Date();
  //     const notificationDate = new Date(date);

  //     if (isNaN(notificationDate.getTime())) {
  //       return 'Recently';
  //     }

  //     const diffInMinutes = Math.floor(
  //       (now.getTime() - notificationDate.getTime()) / (1000 * 60)
  //     );

  //     if (diffInMinutes < 1) return 'Just now';
  //     if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  //     const diffInHours = Math.floor(diffInMinutes / 60);
  //     if (diffInHours < 24) return `${diffInHours}h ago`;

  //     // For older notifications (>24h), show the actual date and time
  //     const options = {
  //       month: 'short',
  //       day: 'numeric',
  //       hour: '2-digit',
  //       minute: '2-digit'
  //     };
  //     return notificationDate.toLocaleDateString(undefined, options);
  //   } catch (e) {
  //     console.error('Error formatting date:', e);
  //     return 'Recently';
  //   }
  // };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const now = new Date();
      const notificationDate = new Date(timestamp);

      if (isNaN(notificationDate.getTime())) {
        return 'Recently';
      }

      const diffInMs = now - notificationDate;
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;

      // Show full date for notifications older than a day
      return notificationDate.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Invalid timestamp format:', error);
      return 'Recently';
    }
  };

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'LIKE':
        return '❤️';
      case 'MESSAGE':
        return '💬';
      case 'MATCH':
        return '💘';
      case 'SHORTLIST':
        return '📌';
      case 'REQUEST':
        return '📩'; // 👺, 📨
      case 'VISIT':
        return '👀';
      case 'PROFILE_APPROVED':
        return '✅'; // Profile approved
      case 'PROFILE_REJECTED':
        return '❌';
      case 'REQUEST ACCEPTANCE':
        return '🎉';
      default:
        return '🔔';
    }
  };

  return (
    <div className="notification-container">
      <div className="notification-icon" onClick={toggleDropdown}>
        <span className={styles.navItem}>
          <img src={NotificationIcon} alt="Notification" />
          Notification
        </span>
        {hasUnread && <span className="notification-badge">•</span>}
      </div>
      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-tabs">
            <div
              className={`tab ${activeTab === 'unread' ? 'active' : ''}`}
              onClick={() => setActiveTab('unread')}
            >
              UNREAD
            </div>
            <div
              className={`tab ${activeTab === 'read' ? 'active' : ''}`}
              onClick={() => setActiveTab('read')}
            >
              READ
            </div>
          </div>
          <div className="notification-list">
            {/* Unread Tab Content */}
            {activeTab === 'unread' && (
              <>
                {notifications.length > 0 ? (
                  <>
                    {notifications.length > 0 && (
                      <div className="mark-all-container">
                        <button
                          className="read-all-btn"
                          onClick={markAllAsRead}
                        >
                          Mark all as read
                        </button>
                      </div>
                    )}
                    {notifications.map((notification, index) => (
                      <div
                        key={`unread-${notification.id}-${index}`}
                        className="notification-item unread"
                        onClick={() => {
                          console.log(
                            'Notification clicked, ID:',
                            notification.id
                          );
                          markAsRead(notification.id);

                          if (notification.senderId) {
                            navigate(`/profileview/${notification.senderId}`);
                          }
                        }}
                      >
                        <div className="notification-content">
                          <div className="notification-icon">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="notification-messagee">
                            {notification.content}
                          </div>
                        </div>
                        <div className="notification-time">
                          {/* {formatTime(notification.receivedAt)} */}
                          {formatTime(notification.receivedAt)}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="no-notifications">
                    No unread notifications
                  </div>
                )}
              </>
            )}

            {/* Read Tab Content */}
            {activeTab === 'read' && (
              <>
                {readNotifications.length > 0 ? (
                  readNotifications.map((notification, index) => (
                    <div
                      key={`read-${notification.id}-${index}`}
                      className="notification-item read"
                      onClick={() => {
                        if (notification.senderId) {
                          navigate(`/profileview/${notification.senderId}`);
                        }
                      }}
                    >
                      <div className="notification-content">
                        <div className="notification-icon">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="notification-message">
                          {notification.content}
                        </div>
                      </div>
                      <div className="notification-time">
                        {formatTime(notification.receivedAt)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-notifications">No read notifications</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {/* Add styles */}
      <style>{`
        .notification-container {
          position: relative;
        }
        .notification-icon {
          cursor: pointer;
          font-size: 16px;
          position: relative;
        }
        .notification-badge {
          position: absolute;
          // top: -9px;
          // right: 100px;
          top: 3px;
          right: 96px;
          color: red;
          font-size: 30px;
        }
        .notification-dropdown {
          position: absolute;
          top: 40px;
          right: -90px;
          width: 320px;
          max-height: 400px;
          overflow-y: auto;
          background-color: #FAF1D9;
          border: 1px solid #d20b0b;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          z-index: 1000;
        }
        .notification-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 5px 15px;
          border-bottom: 1px solid #d20b0b;
          background-color: #FAF1D9;
        }
        .notification-header h3 {
          margin: 0;
          font-size: 16px;
          font-family: 'Teachers', sans-serif;
          color: #d9534f;
        }
        .mark-all-container {
    display: flex;
    justify-content: flex-end;
    padding: 10px 15px;
    border-bottom: 1px solid #d20b0b;
}
        .read-all-btn {
    background: none;
    border: none;
    color: #d20b0b;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    font-family: 'Teachers';
}
        .notification-tabs {
          display: flex;
          border-bottom: 1px solid #d20b0b;
          gap: 30px;
          justify-content: center;
        }
          .notification-content{
          display:flex;
          gap:10px;
      }
        .tab {
          text-align: center;
          padding: 10px 0;
          cursor: pointer;
          padding-top: 5px;
          color: #d20b0b;
          position: relative;
          font-size: 12px;
          font-family: 'Teachers';
          margin: 5px;
        }
 
        .tab.active {
          color: #d20b0b;
        }
        .tab.active:after {
          content: '';
          position: absolute;
          bottom: -5px;
          left: -13%;
          width: 126%;
          height: 3px;
          background-color: #d20b0b;
        }
        .notification-list {
          padding: 0;
          background-color: #FAF1D9;
        }
        .notification-item {
    padding: 20px 18px 33px 18px;
    border-bottom: 1px solid #d20b0b;
    cursor: pointer;
    position: relative;
}
        // .notification-item:hover {
        //   background-color: #FAEFE8;
        // }
        .notification-sender {
          font-weight: 500;
          color: #d20b0b;
          margin-bottom: 5px;
          font-size: 14px;
        }
        .notification-time {
    position: absolute;
    top: 44px;
    left: 51px;
    font-size: 14px;
    color: #d20b0b;
    font-family: 'Teachers';
}
        .notification-messagee{
    font-size: 12px;
    color: #d20b0b;
    font-family: 'Teachers';
}
    .notification-message{
 
    font-size: 12px;
    font-family: 'Teachers';
    color: #d20b0b !important;
}
        .no-notifications {
          padding: 20px;
          text-align: center;
          color: #D20B0B;
        }
      `}</style>
    </div>
  );
};

export default NotificationComponent;
